import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

var _templateObject, _templateObject2, _templateObject3;

var _excluded = ["children", "isOpen", "onClose", "modalLabel", "className", "closeButtonProps", "onClick"];
import { NavigationClose } from '@repay/cactus-icons';
import { border, colorStyle, mediaGTE, radius, space } from '@repay/cactus-theme';
import PropTypes from 'prop-types';
import React from 'react';
import { compose, height, margin, maxHeight, maxWidth, minHeight, minWidth, padding, position, textAlign, width } from 'styled-system';
import Dimmer from '../Dimmer/Dimmer';
import FocusLock from '../FocusLock/FocusLock';
import { omitProps } from '../helpers/omit';
import { usePortal } from '../helpers/portal';
import { trapScroll } from '../helpers/scroll';
import { flexContainerOption, flexItem, gapWorkaround, pickStyles, styledProp, styledUnpoly, styledWithClass } from '../helpers/styled';
import IconButton from '../IconButton/IconButton';
export var modalType = ['action', 'danger', 'warning', 'success'];
// Overflow & flex don't mix well, so we need an intermediate div for scrolling.
var ModalBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var children = props.children,
      isOpen = props.isOpen,
      onClose = props.onClose,
      modalLabel = props.modalLabel,
      className = props.className,
      closeButtonProps = props.closeButtonProps,
      onClick = props.onClick,
      otherProps = _objectWithoutProperties(props, _excluded);

  var dimmerRef = React.useRef(null);
  React.useEffect(function () {
    if (dimmerRef.current) {
      var modal = (ref === null || ref === void 0 ? void 0 : ref.current) || dimmerRef.current.querySelector('[aria-modal]');
      if (modal) modal.focus();
      return trapScroll(dimmerRef);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  var mouseRef = React.useRef(null);

  var onMouseDown = function onMouseDown(event) {
    mouseRef.current = event.target;
  };

  var onDimmerClick = function onDimmerClick(event) {
    // if statement to ignore click-and-drag.
    if (event.target === mouseRef.current) {
      onClose();
    }
  };

  var onModalClick = function onModalClick(event) {
    event.stopPropagation();
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  };

  var closeOnEscape = function closeOnEscape(event) {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return usePortal( /*#__PURE__*/React.createElement(Dimmer, {
    className: className,
    ref: dimmerRef,
    active: isOpen,
    onMouseDown: onMouseDown,
    onClick: onDimmerClick,
    onKeyDown: closeOnEscape
  }, /*#__PURE__*/React.createElement(FocusLock, {
    className: "flex-container"
  }, /*#__PURE__*/React.createElement(ModalPopUp, Object.assign({
    role: "dialog",
    "aria-modal": true,
    "aria-label": modalLabel,
    tabIndex: -1,
    onClick: onModalClick
  }, otherProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement(CloseButton, Object.assign({}, closeButtonProps, {
    onClick: onClose
  }), /*#__PURE__*/React.createElement(NavigationClose, null)), children))));
});
ModalBase.displayName = 'Modal'; // Because there's an intermediate container percentages don't work well, so we
// convert to widths relative to the overlay (same dimensions as the viewport).

var convertPercentToFixed = function convertPercentToFixed(suffix) {
  for (var _len = arguments.length, styleFns = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    styleFns[_key - 1] = arguments[_key];
  }

  var composed = compose.apply(void 0, styleFns);

  var convert = function convert(value) {
    if (value) {
      if (typeof value === 'object') {
        for (var _i = 0, _Object$keys = Object.keys(value); _i < _Object$keys.length; _i++) {
          var key = _Object$keys[_i];
          value[key] = convert(value[key]);
        }
      } else if (typeof value === 'string') {
        value = value.replace(/%/g, suffix);
      }
    }

    return value;
  }; // Note that because these are wrapped in a custom parser, they can't be `compose`d further.


  var fn = function fn(props) {
    return convert(composed(props));
  };

  Object.assign(fn, composed);
  return fn;
}; // Even vertical margins/padding are percentages of parent's width, per CSS spec.


var widths = convertPercentToFixed('vw', width, minWidth, maxWidth, margin, padding);
var heights = convertPercentToFixed('vh', height, minHeight, maxHeight);
var modalStyles = compose(flexItem, flexContainerOption, textAlign);
var variantColors = {
  action: 'callToAction',
  warning: 'warning',
  success: 'success',
  danger: 'error'
};
var ModalPopUp = styledWithClass('div', 'cactus-modal').withConfig(omitProps(widths, heights, modalStyles, 'variant'))(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n  position: relative;\n  box-sizing: border-box;\n  border: ", ";\n  border-radius: ", ";\n  outline: none;\n  margin: 5vw;\n  max-width: 90vw;\n  padding: ", " ", ";\n  padding-top: 64px;\n  overflow-wrap: break-word;\n  word-wrap: break-word;\n  // Will have no effect unless they enable flex display.\n  align-items: center;\n  flex-direction: column;\n\n  ", " {\n    margin: ", ";\n    max-width: 70vw;\n  }\n\n  ", " {\n    margin: ", ";\n    max-width: 50vw;\n    padding: ", " 88px;\n  }\n\n  ", "\n  // Because of how styled-components groups styles, we have to increase\n  // the specificity in order to override the styles in the media queries.\n  && {\n    ", "\n    ", "\n    ", "\n  }\n"])), colorStyle('standard'), function (p) {
  return border(p, variantColors[p.variant], {
    thin: '2px',
    thick: '3px'
  });
}, radius(20), space(7), space(5), mediaGTE('small'), space(5), mediaGTE('medium'), space(7), space(7), gapWorkaround, widths, heights, modalStyles);
var buttonPosition = pickStyles(position, 'top', 'right', 'left');
var CloseButton = styledWithClass(IconButton, 'modal-close-btn').withConfig(omitProps(buttonPosition))(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  right: ", ";\n  top: ", ";\n\n  ", " {\n    right: ", ";\n    top: ", ";\n  }\n\n  && {\n    position: absolute;\n    ", "\n  }\n"])), space(5), space(5), mediaGTE('medium'), space(7), space(7), buttonPosition);
CloseButton.displayName = 'Modal.CloseButton';
CloseButton.propTypes = {
  label: PropTypes.string.isRequired
}; // Passing `iconSize` as a default instead of setting the styles directly,
// because it's defined in the `IconButton` component and local styles override those.

CloseButton.defaultProps = {
  label: 'Close Modal',
  iconSize: ['small', 'small', 'medium']
};
export var Modal = styledUnpoly(ModalBase)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: block;\n  overflow: auto;\n  z-index: 101;\n\n  & > .flex-container {\n    box-sizing: content-box;\n    min-width: 100%;\n    min-height: 100%;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n  }\n"])));
Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(modalType),
  closeButtonProps: PropTypes.shape({
    label: PropTypes.string,
    iconSize: styledProp,
    top: styledProp,
    right: styledProp,
    left: styledProp
  })
};
Modal.defaultProps = {
  modalLabel: 'Modal',
  variant: 'action',
  closeButtonProps: CloseButton.defaultProps
};
export default Modal;